import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Save, Close, BugReport } from '@mui/icons-material';
import { DiffEditor } from '@monaco-editor/react';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import { yamlDiffService } from '../services/yamlDiffService';
import { useSectionStyles } from '@/styles/utilities/hooks';

/**
 * Dialog for creating custom inputspec configurations
 * Allows editing YAML directly and automatically extracts overrides/deletions
 */
const ManualInputspecEditorDialog = ({
  open,
  onClose,
  baseInputspec,
  baseYaml,
  onSave,
  onDebugOpen,
  onDebugDataChange
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  
  // Form state
  const [name, setName] = useState('');
  const [basis, setBasis] = useState('');
  const [cutGlycine, setCutGlycine] = useState(true);
  const [cutTail, setCutTail] = useState(false);
  const [modifiedYaml, setModifiedYaml] = useState('');
  const [yamlError, setYamlError] = useState('');
  
  // Computed differences
  const [overrides, setOverrides] = useState({});
  const [deletions, setDeletions] = useState({});
  
  // Refs for Monaco Editor
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const diffEditorRef = useRef(null);
  
  // Common basis sets
  const basisSets = [
    'STO-3G',
    '3-21G',
    '6-31G',
    '6-31G*',
    '6-31G**',
    '6-31++G**',
    '6-311G*',
    '6-311G**'
  ];
  
  // Initialize form when dialog opens or base changes
  useEffect(() => {
    if (open && baseInputspec && baseYaml) {
      // Set default name based on base
      const timestamp = new Date().toISOString().slice(0, 10);
      setName(`Custom_${baseInputspec.name}_${timestamp}`);
      setBasis(baseInputspec.basis || 'STO-3G');
      setCutGlycine(baseInputspec.cut_glycine !== undefined ? baseInputspec.cut_glycine : true);
      setCutTail(baseInputspec.cut_tail !== undefined ? baseInputspec.cut_tail : false);
      setModifiedYaml(baseYaml);
      setYamlError('');
      setOverrides({});
      setDeletions({});
    }
  }, [open, baseInputspec, baseYaml]);
  
  // Calculate differences when YAML changes
  useEffect(() => {
    if (!modifiedYaml || !baseYaml) {
      const emptyData = { overrides: {}, deletions: {} };
      setOverrides({});
      setDeletions({});
      setYamlError('');
      // Update debug data
      if (onDebugDataChange) {
        onDebugDataChange(emptyData);
      }
      return;
    }
    
    // Validate modified YAML
    const validation = yamlDiffService.validateYAML(modifiedYaml);
    if (!validation.valid) {
      setYamlError(validation.error);
      setOverrides({});
      setDeletions({});
      // Update debug data
      if (onDebugDataChange) {
        onDebugDataChange({ overrides: {}, deletions: {} });
      }
      return;
    }
    
    setYamlError('');
    
    // Calculate differences
    try {
      const diff = yamlDiffService.calculateDiff(baseYaml, modifiedYaml);
      setOverrides(diff.overrides);
      setDeletions(diff.deletions);
      
      // Auto-update debug data whenever it changes
      if (onDebugDataChange) {
        onDebugDataChange({ overrides: diff.overrides, deletions: diff.deletions });
      }
    } catch (error) {
      console.error('Error calculating diff:', error);
      setYamlError('Error calculating differences');
    }
  }, [modifiedYaml, baseYaml]); // Removed onDebugDataChange to prevent infinite loop
  
  // Handle Monaco Diff Editor mount
  const handleEditorDidMount = (editor, monaco) => {
    // Store both the diff editor and modified editor
    diffEditorRef.current = editor;
    editorRef.current = editor.getModifiedEditor();
    monacoRef.current = monaco;
    
    // Add content change listener to the modified editor
    const modifiedEditor = editor.getModifiedEditor();
    modifiedEditor.onDidChangeModelContent((event) => {
      const newValue = modifiedEditor.getValue();
      setModifiedYaml(newValue);
    });
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Dispose the diff editor when component unmounts
      if (diffEditorRef.current) {
        diffEditorRef.current.dispose();
      }
    };
  }, []);
  
  
  const handleSave = async () => {
    if (!name || !basis) {
      setYamlError('Name and basis are required');
      return;
    }
    
    if (yamlError) {
      return;
    }
    
    const inputspecData = {
      name,
      basis,
      source_inputspec_id: baseInputspec.id_,
      overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      deletions: Object.keys(deletions).length > 0 ? deletions : undefined,
      cut_glycine: cutGlycine,
      cut_tail: cutTail
    };
    
    await onSave(inputspecData);
    // Dialog will be closed by parent after successful save
  };
  
  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title="Create Custom Configuration"
      variant="form"
      width={theme.spacing(150)} // 1200px in comfortable density
      height="auto"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        {/* Configuration Settings */}
        <Box>
          <Typography variant="caption" sx={sectionStyles.sectionHeader}>
            CONFIGURATION SETTINGS
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr',
            gap: theme.spacing(2),
            mt: theme.spacing(1)
          }}>
            <TextField
              label="Configuration Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
              fullWidth
              InputProps={{
                sx: { height: theme.spacing(5) }
              }}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>Basis Set</InputLabel>
              <Select
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
                label="Basis Set"
                sx={{ height: theme.spacing(5) }}
              >
                {basisSets.map(b => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cutGlycine}
                  onChange={(e) => setCutGlycine(e.target.checked)}
                  size="small"
                />
              }
              label="Cut Glycine"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={cutTail}
                  onChange={(e) => setCutTail(e.target.checked)}
                  size="small"
                />
              }
              label="Cut Tail"
            />
          </Box>
        </Box>
        
        <Divider />
        
        {/* YAML Editor Section */}
        <Box>
          <Typography variant="caption" sx={sectionStyles.sectionHeader}>
            YAML CONFIGURATION
          </Typography>
          <Paper
            variant="outlined"
            sx={{ 
              mt: theme.spacing(1),
              height: theme.spacing(50),
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <DiffEditor
              height="100%"
              language="yaml"
              original={baseYaml || ''}
              modified={modifiedYaml || ''}
              onMount={handleEditorDidMount}
              theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs'}
              options={{
                originalEditable: false,
                renderSideBySide: true,
                renderSideBySideInlineBreakpoint: 0,
                minimap: { enabled: false },
                wordWrap: 'on',
                lineNumbers: 'on',
                folding: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                fontSize: 12,
                fontFamily: 'Roboto Mono, monospace',
                tabSize: 2,
                insertSpaces: true,
                renderWhitespace: 'selection',
                quickSuggestions: false,
                suggestOnTriggerCharacters: false,
                acceptSuggestionOnCommitCharacter: false,
                parameterHints: { enabled: false },
                hover: { enabled: false },
                occurrencesHighlight: false,
                selectionHighlight: false,
                renderValidationDecorations: 'off'
              }}
            />
          </Paper>
          
          {yamlError && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.error.main,
                mt: theme.spacing(0.5),
                display: 'block',
                fontSize: theme.typography.fontSize.xs
              }}
            >
              {yamlError}
            </Typography>
          )}
          
        </Box>
        
        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: theme.spacing(1),
          justifyContent: 'space-between',
          pt: theme.spacing(1)
        }}>
          <Tooltip title="View computed differences (debug)">
            <IconButton
              onClick={() => onDebugOpen && onDebugOpen({ overrides, deletions })}
              size="small"
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main
                }
              }}
            >
              <BugReport fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
            <Button
              onClick={onClose}
              startIcon={<Close />}
              sx={{ minWidth: theme.spacing(10) }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<Save />}
              disabled={!name || !basis || !!yamlError}
              sx={{ minWidth: theme.spacing(10) }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </DraggableDialog>
  );
};

export default ManualInputspecEditorDialog;